import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  Paper,
  Divider,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AddCircleOutline as RequestIcon,
  History as HistoryIcon,
  PlayCircleOutline as ActiveIcon,
  Approval as ApprovalIcon,
  AccountBalance as AccountsIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  SupervisorAccount as ManageSessionsIcon,
  Assignment as RequestHistoryIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { motion } from 'motion/react';
import { sectionVariants } from '../../animations/variants';
import { hasMinRole, ROLES } from '../../utils/roles';
import { removeJITToken, getJITUser } from '../../services/jitApi';
import { BASE_DOMAIN } from '../../utils/constants';

const drawerWidth = 260;
const collapsedWidth = 72;

// Helper function to derive display name from email
const getDisplayNameFromEmail = (email) => {
  if (!email) return 'User';
  const localPart = email.split('@')[0];
  return localPart
    .replace(/[._-]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const JITSidebar = ({ user, pendingCount = 0, mobileOpen = false, onMobileToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleLogout = () => {
    const jitUser = getJITUser();
    const realm = jitUser?.realm;

    removeJITToken();

    if (realm) {
      const baseDomain = BASE_DOMAIN.replace(/:\d+$/, '');
      const hostname = window.location.hostname;

      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        window.location.href = `http://apps.localhost:3000`;
      } else {
        window.location.href = `https://${realm}.${baseDomain}`;
      }
    } else {
      const baseDomain = BASE_DOMAIN.replace(/:\d+$/, '');
      window.location.href = `https://apps.${baseDomain}`;
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleNavigate = (path) => {
    navigate(path);
    // Auto-close sidebar on mobile after navigation
    if (isMobile && onMobileToggle) {
      onMobileToggle();
    }
  };

  const userNavigationItems = [
    { name: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { name: 'Request Access', icon: <RequestIcon />, path: '/request' },
    { name: 'My Requests', icon: <HistoryIcon />, path: '/my-requests' },
    { name: 'Active Sessions', icon: <ActiveIcon />, path: '/active-sessions' },
  ];

  const approverNavigationItems = [
    { name: 'Pending Approvals', icon: <ApprovalIcon />, path: '/approvals', badge: pendingCount },
    { name: 'Request History', icon: <RequestHistoryIcon />, path: '/request-history' },
    { name: 'Manage Sessions', icon: <ManageSessionsIcon />, path: '/manage-sessions' },
    { name: 'Owned Accounts', icon: <AccountsIcon />, path: '/owned-accounts' },
  ];

  const navigationItems = hasMinRole(user?.role, ROLES.APPROVER)
    ? [...userNavigationItems, ...approverNavigationItems]
    : userNavigationItems;

  // On mobile: never collapsed, always full width in overlay
  const effectiveCollapsed = isMobile ? false : isCollapsed;

  const drawerContent = (
    <>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: effectiveCollapsed ? 'center' : 'space-between',
          padding: effectiveCollapsed ? '16px 8px' : '16px 20px',
          minHeight: 72,
          maxHeight: 72,
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E5E5E5',
          borderRadius: 0,
          flexShrink: 0,
        }}
      >
        {!effectiveCollapsed && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <img
              src="https://www.cloudkeeper.com/themes/cloud_theme/images/Cloudkeeper_New.svg"
              alt="CloudKeeper"
              style={{
                height: '28px',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: '#4698D3',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                mt: 0.5,
              }}
            >
              JIT Access Portal
            </Typography>
          </Box>
        )}
        {isMobile ? (
          <IconButton
            onClick={onMobileToggle}
            sx={{
              color: '#253E66',
              backgroundColor: '#F9FBFD',
              border: '1px solid #E5E5E5',
              borderRadius: '6px',
              width: 32,
              height: 32,
              '&:hover': {
                backgroundColor: '#F0F4F8',
                borderColor: '#D9DBDC',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : (
          <Tooltip title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}>
            <IconButton
              onClick={() => setIsCollapsed(!isCollapsed)}
              sx={{
                color: '#253E66',
                backgroundColor: '#F9FBFD',
                border: '1px solid #E5E5E5',
                borderRadius: '6px',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: '#F0F4F8',
                  borderColor: '#D9DBDC',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Paper>

      {/* Navigation List */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 1.5,
          py: 2,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: '#F9FBFD', borderRadius: '2px' },
          '&::-webkit-scrollbar-thumb': { background: '#D9DBDC', borderRadius: '2px' },
        }}
      >
        <List sx={{ p: 0 }}>
          {navigationItems.map((item, index) => (
            <React.Fragment key={item.path}>
              {hasMinRole(user?.role, ROLES.APPROVER) && index === userNavigationItems.length && (
                <Box sx={{ py: 1 }}>
                  <Divider sx={{ borderColor: '#E5E5E5' }} />
                  {!effectiveCollapsed && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#999999',
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        px: 2,
                        pt: 1.5,
                        display: 'block',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Approver
                    </Typography>
                  )}
                </Box>
              )}
              <ListItem disablePadding sx={{
                '@keyframes fadeInItem': {
                  from: { opacity: 0, transform: 'translateY(8px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
                animation: `fadeInItem 0.3s ease-out ${index * 0.05}s both`,
                mb: 0.5,
              }}>
                <Tooltip title={effectiveCollapsed ? item.name : ''} placement="right">
                  <ListItemButton
                    onClick={() => handleNavigate(item.path)}
                    selected={isActive(item.path)}
                    sx={{
                      borderRadius: '6px',
                      color: isActive(item.path) ? '#4698D3' : '#666666',
                      backgroundColor: isActive(item.path) ? 'rgba(70, 152, 211, 0.08)' : 'transparent',
                      border: isActive(item.path) ? '1px solid rgba(70, 152, 211, 0.2)' : '1px solid transparent',
                      '&:hover': {
                        backgroundColor: isActive(item.path)
                          ? 'rgba(70, 152, 211, 0.12)'
                          : '#F9FBFD',
                        color: isActive(item.path) ? '#4698D3' : '#253E66',
                      },
                      justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                      px: effectiveCollapsed ? 1.5 : 2,
                      py: 1.25,
                      minHeight: 44,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: 'inherit',
                        minWidth: effectiveCollapsed ? 'auto' : 36,
                        justifyContent: 'center',
                        '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
                      }}
                    >
                      {item.badge > 0 ? (
                        <Badge badgeContent={item.badge} color="error">
                          {item.icon}
                        </Badge>
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    {!effectiveCollapsed && (
                      <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: isActive(item.path) ? 600 : 500,
                          fontSize: '0.875rem',
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            </React.Fragment>
          ))}

          {/* Mobile Logout Button - visible text logout item in the nav list */}
          {isMobile && (
            <>
              <Box sx={{ py: 1 }}>
                <Divider sx={{ borderColor: '#E5E5E5' }} />
              </Box>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={handleLogout}
                  sx={{
                    borderRadius: '6px',
                    color: '#E74C3C',
                    '&:hover': {
                      backgroundColor: 'rgba(231, 76, 60, 0.08)',
                      color: '#C0392B',
                    },
                    px: 2,
                    py: 1.25,
                    minHeight: 44,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: 'inherit',
                      minWidth: 36,
                      justifyContent: 'center',
                      '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
                    }}
                  >
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Logout"
                    primaryTypographyProps={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Box>

      {/* Bottom section - User Info */}
      <Box sx={{
        flexShrink: 0,
        position: 'sticky',
        bottom: 0,
        backgroundColor: '#FFFFFF',
        zIndex: 1,
      }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#F9FBFD',
            borderTop: '1px solid #E5E5E5',
            padding: effectiveCollapsed ? '12px 8px' : '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: effectiveCollapsed ? 'center' : 'space-between',
            borderRadius: 0,
            minHeight: 68,
            maxHeight: 68,
          }}
        >
          {!effectiveCollapsed && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: hasMinRole(user?.role, ROLES.SSO_ADMIN) ? '#7B1FA2' : hasMinRole(user?.role, ROLES.APPROVER) ? '#D64794' : '#4698D3',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  flexShrink: 0,
                }}
              >
                {getDisplayNameFromEmail(user?.email).charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                <Typography
                  sx={{
                    color: '#253E66',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getDisplayNameFromEmail(user?.email)}
                </Typography>
                <Typography
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.7rem',
                    lineHeight: 1,
                    fontWeight: 500,
                  }}
                >
                  {hasMinRole(user?.role, ROLES.SSO_ADMIN) ? 'SSO Admin' : hasMinRole(user?.role, ROLES.APPROVER) ? 'Approver' : 'User'}
                </Typography>
              </Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Tooltip title="Logout" placement="top">
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: '#E74C3C',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  width: 32,
                  height: 32,
                  '&:hover': {
                    backgroundColor: 'rgba(231, 76, 60, 0.08)',
                    borderColor: '#E74C3C',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <LogoutIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>
      </Box>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={onMobileToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#FFFFFF',
            borderRight: '1px solid #E5E5E5',
            borderRadius: 0,
            overflowX: 'hidden',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={true}
      sx={{
        width: isCollapsed ? collapsedWidth : drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isCollapsed ? collapsedWidth : drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: '#FFFFFF',
          borderRight: '1px solid #E5E5E5',
          borderRadius: 0,
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
          overflowY: 'hidden',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1200,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default JITSidebar;
